import { Skeleton } from "@/components/skeleton";
import { cn } from "@/utils/classnames";
import NextImage from "next/image";

type Props = Omit<React.ComponentProps<typeof NextImage>, "src" | "loading"> & {
  src?: string | null;
  size?: "xsmall" | "small" | "large";
  loading?: boolean;
};

const imagesSizes = {
  xsmall: {
    width: 40,
    height: 40,
  },
  small: {
    width: 192,
    height: 192,
  },
  large: {
    width: 460,
    height: 460,
  },
};

export const Image = ({
  src,
  loading = false,
  size = "small",
  quality = 75,
  className,
  alt,
  ...rest
}: Props) => {
  const defaultClasses = cn(
    "aspect-square overflow-hidden",
    {
      "min-w-[40px] max-w-[40px] border-none rounded-sm bg-transparent":
        size === "xsmall",
      "min-w-[96px] max-w-[192px] shadow-[0_2px_10px_0_rgba(0,0,45,0.09)] rounded-jade-medium border border-(--theme-color-border-low) bg-[#f0f0f3]":
        size === "small",
      "min-w-[280px] max-w-[460px] shadow-[0_5px_20px_0_rgba(0,0,45,0.09)] rounded-jade-xlarge border border-(--theme-color-border-low) bg-[#f0f0f3]":
        size === "large",
    },
    className
  );

  return loading ? (
    <div className={defaultClasses}>
      <Skeleton />
    </div>
  ) : (
    <div
      className={cn(
        "transition-colors duration-100 group-hover/product-card:border-white",
        defaultClasses
      )}
    >
      {src ? (
        <NextImage
          src={src}
          width={imagesSizes[size].width}
          height={imagesSizes[size].height}
          alt={alt}
          className="h-full w-full object-cover"
          quality={quality}
          {...rest}
        />
      ) : (
        <div
          className="h-full w-full border border-(--theme-color-border-low) bg-[#f0f0f3] bg-[url('/pattern-tile.svg')] bg-repeat"
          {...rest}
        ></div>
      )}
    </div>
  );
};
